import { Device } from '@sb/integrations/types/DeviceInterface';
import { type TCPModbusConnection } from '@sb/integrations/utils/modbus';
import { makeNamespacedLog } from '@sb/log';
import { timeout, wait } from '@sb/utilities';

import { convertMetersPerSecond } from '../../shared/convertMetersPerSecond';
import type {
  GasFlowConfig,
  MillerWeldMachineCommand,
} from '../types/MillerWeldMachineCommands';
import type { MillerWeldMachineConfiguration } from '../types/MillerWeldMachineConfiguration';
import { MillerWeldMachineState } from '../types/MillerWeldMachineState';

import {
  Coils,
  DiscreteInputs,
  HoldingRegisters,
  INITIALIZATION_TIMEOUT_MS,
  InputRegisters,
  RETRY_INTERVAL_MS,
  WELD_PROGRAM_LOAD_MS,
  WeldMachineModeFromEnum,
  WireSize,
  WireType,
  GasType,
  WireSizeRaw,
  WireTypeRaw,
  GasTypeRaw,
  WeldMachineModeToEnum,
  WAIT_FOR_MACHINE_TO_RESPOND_MS,
  WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS,
} from './constants';
import {
  convertArcLength,
  convertFlowTime,
  convertHeatInput,
  convertRawHeat,
  convertVoltageScale,
  convertWeldTravelSpeed,
} from './conversions';
import { MillerWeldMachineKinematics } from './MillerWeldMachineKinematics';

const log = makeNamespacedLog('MillerWeldMachine');

type MillerWeldMachineEvents = {
  stateChange(): void;
  statusUpdate: void;
  destroy(reason: string): void;
  disconnect: void;
  stop(): void;
};

/**
 * Miller auto deltaweld basic
 *
 * Miller modbus manual https://drive.google.com/file/d/1Crw9W3lW8ztJhKeRwn52TYPZISKVeBwu/view
 */
export class MillerWeldMachine extends Device<
  MillerWeldMachineState,
  MillerWeldMachineEvents
> {
  public readonly kind = 'MillerWeldMachine';

  public readonly stateSchema = MillerWeldMachineState;

  private state: MillerWeldMachineState = {
    kind: 'MillerWeldMachine',
    isWelding: false,
    isConnected: false,
    isDryRunMode: true,
    coils: {
      weldDisable: true, // Weld disable does not actually work. However, we want to track this because - if it is active - then the weld settings won't actually work.
      contactorOn: false,
      gasEnable: false,
      motorForward: false,
      motorRetract: false,
      touchSenseEnable: false,
    },
    discreteInputs: {
      arcDetected: false,
      contactorStatus: false,
      gasStatus: false,
      feedForwardStatus: false,
      feedRetractStatus: false,
      touchSenseEnabled: false,
      wireTouched: false,
      weldStateStatus: false,
      synergicProcess: false,
      errorActive: false,
      pSConnected: false,
      ready: false,
    },
    inputRegisters: {
      // Actual. Table 3-2
      actualWFS: 0,
      actualArcVoltage: 0,
      actualArcCurrent: 0,
      actualGasFlow: 0,
      actualRawHeat: 0,
      actualHeatInput: 0,
      // Presets: Values as set on machine (either programmatically/on-bot or on machine, elsewhere)
      presetWFS: 0,
      presetVoltage: 0,
      presetArcLength: 0,
      presetArcControl: 0,
      memorySlot: 0,
      activeProcess: 'constantVoltage', // Table 3-4
      activeWireType: WireTypeRaw.Aluminum_4943, // Table 3-5
      activeWireSize: WireSizeRaw.Size_0_030, // Table 3-6
      activeGasType: GasTypeRaw.Argon_100, // Table 3-7
      errorCode: 0,
    },
    holdingRegisters: {
      presetWFS: 0,
      presetVoltage: 0,
      presetArcLength: 0,
      presetArcControl: 0,
      selectMemorySlot: 0,
      weldProcess: 'constantVoltage', // Table 3-4
      weldWireType: WireTypeRaw.Aluminum_4943, // Table 3-5
      weldWireSize: WireSizeRaw.Size_0_030, // Table 3-6
      weldGasType: GasTypeRaw.Argon_100, // Table 3-7
      weldTravelSpeed: 0,
    },
  };

  public readonly kinematics = new MillerWeldMachineKinematics();

  private isRunning = true;

  private stopListeningForChanges?: () => void;

  /**
   * Canonical array ordering of modbus addresses to which we subscribe.
   */
  private modbusSubscriptionAddresses = {
    coils: [
      Coils.WeldDisable,
      Coils.ContactorOn,
      Coils.GasEnable,
      Coils.MotorForward,
      Coils.MotorRetract,
      Coils.TouchSenseEnable,
    ],
    discreteInputs: [
      DiscreteInputs.ArcDetected,
      DiscreteInputs.ContactorStatus,
      DiscreteInputs.GasStatus,
      DiscreteInputs.FeedForwardStatus,
      DiscreteInputs.FeedRetractStatus,
      DiscreteInputs.TouchSenseEnabled,
      DiscreteInputs.WireTouched,
      DiscreteInputs.WeldStateStatus,
      DiscreteInputs.SynergicProcess,
      DiscreteInputs.ErrorActive,
      DiscreteInputs.PSConnected,
      DiscreteInputs.Ready,
    ],
    inputRegisters: [
      InputRegisters.ActualWFS,
      InputRegisters.ActualArcVoltage,
      InputRegisters.ActualArcCurrent,
      InputRegisters.ActualGasFlow,
      InputRegisters.RawHeat,
      InputRegisters.HeatInput,
      InputRegisters.PresetWFS,
      InputRegisters.PresetVoltage,
      InputRegisters.PresetArcLength,
      InputRegisters.PresetArcControl,
      InputRegisters.MemorySlot,
      InputRegisters.ActiveProcess,
      InputRegisters.ActiveWiretype,
      InputRegisters.ActiveWireSize,
      InputRegisters.ActiveGasType,
      InputRegisters.ErrorCode,
    ],
    holdingRegisters: [
      HoldingRegisters.PresetWFS,
      HoldingRegisters.PresetVoltage,
      HoldingRegisters.PresetArcLength,
      HoldingRegisters.PresetArcControl,
      HoldingRegisters.SelectMemorySlot,
      HoldingRegisters.WeldProcess,
      HoldingRegisters.WeldWireType,
      HoldingRegisters.WeldWireSize,
      HoldingRegisters.WeldGasType,
      HoldingRegisters.WeldTravelSpeed,
    ],
  };

  public constructor(
    protected readonly modbus: TCPModbusConnection,
    protected readonly config: MillerWeldMachineConfiguration,
  ) {
    super();
    this.kinematics.config = config;
  }

  public async initialize() {
    while (this.isRunning) {
      try {
        await this.initializeOnce();
        this.listenForChanges();
        await this.race('disconnect', 'destroy');
      } catch (error) {
        log.warn('init.failed', 'Failed to initialize', error);
        this.modbus.stop();
        await wait(RETRY_INTERVAL_MS);
      }
    }
  }

  public async executeCommand(
    command: MillerWeldMachineCommand,
  ): Promise<void> {
    log.info('Executing weld machine command: ', command.kind, { command });

    switch (command.subCommand.kind) {
      case 'startArc':
        await this.startArc(command.subCommand);
        break;
      case 'stopArc':
        await this.stopArc(command.subCommand);
        break;
      case 'startGas':
        await this.startGas(command.subCommand);
        break;
      case 'stopGas':
        await this.stopGas(command.subCommand);
        break;
      case 'startWireFeed':
        await this.startWireFeed(command.subCommand);
        break;
      case 'stopWireFeed':
        await this.stopWireFeed(command.subCommand);
        break;
      case 'setDryRunMode':
        await this.setDryRunMode(command.subCommand);
        break;
      case 'setWeldParameters':
        await this.setWeldParameters(command.subCommand);
        break;
      default: {
        const _: never = command.subCommand;

        return _;
      }
    }

    return Promise.resolve();
  }

  public stop() {
    log.info('stop', 'Stopping', { ready: this.state.discreteInputs.ready });

    if (this.state.discreteInputs.ready) {
      this.stopArc({ kind: 'stopArc' });
    }

    this.emit('stop');
  }

  public update(): MillerWeldMachineState {
    // ? Consider adding a "last message" timestamp to the state to help with debugging. Throw an error in case haven't received state recently.
    // Sampled on read rather than tracked by an event: the Modbus layer
    // reconnects on its own and emits nothing for the socket going down.
    this.state.isConnected = this.modbus.isConnected();

    return this.state;
  }

  public destroy(reason: string) {
    log.info('destroy', 'Destroying', { reason });
    super.destroy(reason);
    this.modbus.stop();
    this.isRunning = false;
    this.stopListeningForChanges?.();
  }

  private async setWeldParameters(
    cmd: Extract<
      MillerWeldMachineCommand['subCommand'],
      { kind: 'setWeldParameters' }
    >,
  ) {
    log.info('setWeldParameters', 'Setting weld parameters.', {
      cmd,
      dryRunMode: this.state.isDryRunMode,
    });

    if (this.state.isDryRunMode) {
      log.warn(
        'setWeldParameters',
        'Dry run mode is active. Skipping weld parameter setting.',
      );

      return Promise.resolve();
    }

    this.assertReady();

    if (this.state.discreteInputs.contactorStatus) {
      throw new Error(`Cannot set weld parameters: Contactor already on.`);
    }

    if (this.state.discreteInputs.weldStateStatus) {
      throw new Error(`Cannot set weld parameters: Welding already active.`);
    }

    if (this.state.discreteInputs.gasStatus) {
      throw new Error(
        `Cannot set weld parameters: Gas is already on. This is an unexpected state and should be investigated.`,
      );
    }

    // Set weld program parameters (process + wire + gas + size)
    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.WeldProcess,
      registerValue: WeldMachineModeToEnum[cmd.mode],
    });

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.WeldWireType,
      registerValue: cmd.wireType,
    });

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.WeldWireSize,
      registerValue: cmd.wireSize,
    });

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.WeldGasType,
      registerValue: cmd.gasType,
    });

    // Valid voltage limits based on duty cycle rating. See 3.4.1.8.
    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.PresetVoltage,
      registerValue: convertVoltageScale.toWeldMachine(cmd.voltage),
    });

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.PresetArcLength,
      registerValue: convertArcLength.toWeldMachine(cmd.arcLength),
    });

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.PresetArcControl,
      registerValue: cmd.arcControl,
    });

    // Does not appear to be respected/registered. However, we will set it anyway because it doesn't hurt.
    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.WeldTravelSpeed,
      registerValue: convertWeldTravelSpeed.toWeldMachine(cmd.weldTravelSpeed),
    });

    const wfs = convertMetersPerSecond.toInchesPerMinute(cmd.wireFeedSpeed);

    this.modbus.setRegisterValue({
      registerAddress: HoldingRegisters.PresetWFS,
      registerValue: wfs,
    });

    // Shielding gas timing. Absent when the caller has not opted in, which
    // leaves the machine's existing sequence settings untouched.
    if (cmd.preflow) {
      this.setGasFlow(
        {
          enable: HoldingRegisters.SequencePreflowEnable,
          time: HoldingRegisters.SequencePreflowTime,
        },
        cmd.preflow,
      );
    }

    if (cmd.postflow) {
      this.setGasFlow(
        {
          enable: HoldingRegisters.SequencePostflowEnable,
          time: HoldingRegisters.SequencePostflowTime,
        },
        cmd.postflow,
      );
    }

    // Takes up to 200ms to load the program.
    // Section 3.4.3 "In the case of using single register writes, there is a 200 ms delay after the last program parameter is received before the power source will respond to the request."
    await wait(WELD_PROGRAM_LOAD_MS);

    return Promise.resolve();
  }

  /**
   * Write one shielding gas flow's enable bit and duration.
   *
   * A disabled flow is written as an explicit 0 rather than skipped: sequence
   * settings persist on the machine between routines, so skipping would let a
   * previous weld's flow carry into one that expects none.
   *
   * The duration goes out regardless of the enable bit, matching how voltage and
   * arc length are always sent regardless of the selected mode.
   */
  private setGasFlow(
    registers: { enable: HoldingRegisters; time: HoldingRegisters },
    config: GasFlowConfig,
  ) {
    this.modbus.setRegisterValue({
      registerAddress: registers.enable,
      registerValue: config.enabled ? 1 : 0,
    });

    this.modbus.setRegisterValue({
      registerAddress: registers.time,
      registerValue: convertFlowTime.toWeldMachine(config.duration),
    });
  }

  /**
   * Start gas + wire feed + turn contactor on.
   *
   * Turning contactor on does all 3: start arc, start gas, start wire feed.
   */
  private async startArc(
    cmd: Extract<MillerWeldMachineCommand['subCommand'], { kind: 'startArc' }>,
  ) {
    log.info('startArc', 'Starting welding...', {
      cmd,
      dryRunMode: this.state.isDryRunMode,
    });

    if (this.state.isDryRunMode) {
      log.warn(
        'startArc',
        'Dry run mode is active. Skipping weld start command.',
      );

      return Promise.resolve();
    }

    this.assertReady();

    if (this.state.discreteInputs.errorActive) {
      // Not clear what to do about an error state other than to throw an error. E.g. how to fix the error on the machine?
      throw new Error(
        `Cannot start weld arc: Weld machine has an active error (${this.state.inputRegisters.errorCode}).`,
      );
    }

    if (this.state.discreteInputs.contactorStatus) {
      throw new Error(`Cannot start weld arc: Contactor already on.`);
    }

    if (this.state.discreteInputs.weldStateStatus) {
      throw new Error(`Cannot start weld arc: Welding already active.`);
    }

    if (this.state.discreteInputs.gasStatus) {
      throw new Error(
        `Cannot start weld arc: Gas is already on. This is an unexpected state and should be investigated.`,
      );
    }

    this.modbus.setCoilValue({
      coilAddress: Coils.ContactorOn,
      coilValue: true,
    });

    // Wait for arc to start
    // See 3.4.2.1. "This signal is typically used to ensure that an arc is established before the robot moves along the weld path."
    const waitForArcToStart = async () => {
      while (true) {
        if (this.state.discreteInputs.arcDetected) {
          log.info('startArc', 'Arc detected.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(
      WAIT_FOR_MACHINE_TO_RESPOND_MS,
      'Failed to start welding Arc. No arc detected.',
    ).race(waitForArcToStart());

    return Promise.resolve();
  }

  /**
   * Turn contactor off + stop wire feed + Stop gas
   *
   * Turning contactor off does all 3: stop arc, stop wire feed, stop gas.
   *
   * NOTE This command will still run in dry run mode. Do not want to potentially leave the welder running in a dangerous state. If the welder is not running, will not do anything.
   */
  private async stopArc(
    cmd: Extract<MillerWeldMachineCommand['subCommand'], { kind: 'stopArc' }>,
  ) {
    log.info('stopArc', 'Stopping welding...', {
      cmd,
      dryRunMode: this.state.isDryRunMode,
    });

    if (this.state.isDryRunMode) {
      log.warn(
        'stopArc',
        'Dry run mode is active. However, will still stop arc if running.',
      );
    }

    // In live mode skip ready check
    if (!this.state.isDryRunMode) {
      this.assertReady();
    }

    log.info('stopArc', 'Stopping welding arc...');

    // Stop the arc.
    this.modbus.setCoilValue({
      coilAddress: Coils.ContactorOn,
      coilValue: false,
    });

    const waitForArcToStop = async () => {
      while (true) {
        if (
          !this.state.discreteInputs.contactorStatus &&
          !this.state.discreteInputs.weldStateStatus &&
          !this.state.discreteInputs.arcDetected
        ) {
          log.info('stopArc', 'Arc stopped.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(
      WAIT_FOR_MACHINE_TO_RESPOND_MS,
      'Failed to stop welding Arc.',
    ).race(waitForArcToStop());
  }

  private async startGas(
    cmd: Extract<MillerWeldMachineCommand['subCommand'], { kind: 'startGas' }>,
  ) {
    log.info('startArc', 'Starting gas...', {
      cmd,
      dryRunMode: this.state.isDryRunMode,
    });

    this.assertReady();

    if (this.state.discreteInputs.gasStatus) {
      throw new Error(
        `Cannot start gas: Gas is already on. This is an unexpected state and should be investigated.`,
      );
    }

    this.modbus.setCoilValue({
      coilAddress: Coils.GasEnable,
      coilValue: true,
    });

    const waitForGasToStart = async () => {
      while (true) {
        if (this.state.discreteInputs.gasStatus) {
          log.info('startArc', 'Gas turned on.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(
      WAIT_FOR_MACHINE_TO_RESPOND_MS,
      'Failed to start welding Arc. Gas could not be turned on.',
    ).race(waitForGasToStart());

    return Promise.resolve();
  }

  /**
   * Stop the gas.
   *
   * NOTE Gas should be stopped as part of stop arc command. However, leave this as a separate command in case.
   * NOTE This command will still run in dry run mode. Do not want to potentially leave the welder running in a dangerous state. If the welder is not running, will not do anything.
   */
  private async stopGas(
    cmd: Extract<MillerWeldMachineCommand['subCommand'], { kind: 'stopGas' }>,
  ) {
    log.info('stopGas', 'Stopping gas...', {
      cmd,
      dryRunMode: this.state.isDryRunMode,
    });

    if (this.state.isDryRunMode) {
      log.warn(
        'stopArc',
        'Dry run mode is active. However, will still stop gas if running.',
      );
    }

    this.assertReady();

    log.info('stopArc', 'Stopping gas...');

    // Stop the gas.
    this.modbus.setCoilValue({
      coilAddress: Coils.GasEnable,
      coilValue: false,
    });

    const waitForGasToStop = async () => {
      while (true) {
        if (!this.state.discreteInputs.gasStatus) {
          log.info('stopArc', 'Gas stopped.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(WAIT_FOR_MACHINE_TO_RESPOND_MS, 'Failed to stop gas.').race(
      waitForGasToStop(),
    );
  }

  /**
   * Start wire feed.
   *
   * NOTE We do not need to start wire feed before starting the arc. Turning the contactor on starts the arc automatically.
   * NOTE We only want to move the wire feed forward. The motor does not appear to support it. Moreover, if we could then the wire would get tangled.
   */
  private async startWireFeed(
    cmd: Extract<
      MillerWeldMachineCommand['subCommand'],
      { kind: 'startWireFeed' }
    >,
  ) {
    log.info('startWireFeed', 'Starting wire feed...', {
      cmd,
      status: this.state.discreteInputs,
      dryRunMode: this.state.isDryRunMode,
    });

    this.assertReady();

    // Stop all wire feeds before starting a new one.
    // Handles case where switching from forward to backward and vice versa.
    await this.stopWireFeed({ kind: 'stopWireFeed' });

    let wfs: number | undefined;

    if (cmd.wireFeedSpeed) {
      wfs = convertMetersPerSecond.toInchesPerMinute(cmd.wireFeedSpeed);
    }

    const isMotorForward = cmd.direction === 'forward';

    if (wfs != null) {
      this.modbus.setRegisterValue({
        registerAddress: HoldingRegisters.PresetWFS,
        registerValue: wfs,
      });
    }

    this.modbus.setCoilValue({
      coilAddress: Coils.MotorForward,
      coilValue: isMotorForward,
    });

    this.modbus.setCoilValue({
      coilAddress: Coils.MotorRetract,
      coilValue: !isMotorForward,
    });

    const waitForMotor = async () => {
      while (true) {
        if (
          this.state.discreteInputs.feedForwardStatus === isMotorForward &&
          this.state.discreteInputs.feedRetractStatus === !isMotorForward
        ) {
          log.info('startWireFeed', 'Motor started in correct direction.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(
      WAIT_FOR_MACHINE_TO_RESPOND_MS,
      'Failed to start wire feeder.',
    ).race(waitForMotor());

    return Promise.resolve();
  }

  /**
   * Stop wire feed.
   */
  private async stopWireFeed(
    cmd: Extract<
      MillerWeldMachineCommand['subCommand'],
      { kind: 'stopWireFeed' }
    >,
  ) {
    log.info('stopWireFeed', 'Stopping wire feed...', {
      cmd,
      status: this.state.discreteInputs,
      dryRunMode: this.state.isDryRunMode,
    });

    this.assertReady();

    this.modbus.setCoilValue({
      coilAddress: Coils.MotorForward,
      coilValue: false,
    });

    this.modbus.setCoilValue({
      coilAddress: Coils.MotorRetract,
      coilValue: false,
    });

    const waitForMotor = async () => {
      while (true) {
        if (
          !this.state.discreteInputs.feedForwardStatus &&
          !this.state.discreteInputs.feedRetractStatus
        ) {
          log.info('stopWireFeed', 'Wire feeder stopped.');

          return true;
        }

        await wait(WAIT_FOR_MACHINE_TO_RESPOND_POLL_INTERVAL_MS);
      }
    };

    await timeout(
      WAIT_FOR_MACHINE_TO_RESPOND_MS,
      'Failed to stop wire feeder.',
    ).race(waitForMotor());

    return Promise.resolve();
  }

  /**
   * Put machine and equipment in dry-run mode.
   *
   * NOTE Miller machine has a "weld disable" coil that is supposed to disable welding. However, it does not work and in fact it can cause unsafe situations (e.g. welder runs with erratic voltage, etc.). Therefore, we simply set the isDryRunMode flag and do not use the weld disable coil.
   */
  private async setDryRunMode(
    cmd: Extract<
      MillerWeldMachineCommand['subCommand'],
      { kind: 'setDryRunMode' }
    >,
    reason = 'Setting dry run mode...',
  ) {
    const { isDryRunMode } = cmd;

    // Already there: nothing to write and nothing worth logging. Lives here
    // rather than in a caller so every path into the flag gets it.
    if (this.state.isDryRunMode === isDryRunMode) {
      return Promise.resolve();
    }

    log.info('setDryRunMode', reason, {
      isDryRunMode,
      status: this.state.discreteInputs,
    });

    // The active-machine guard only applies to *arming* live weld. Refusing to
    // enter dry run while the machine is active protects nothing, and it would
    // drop the reset in the one case that matters most: an E-Stop mid-weld,
    // when the discrete inputs are by definition still hot.
    if (!isDryRunMode && this.machineIsActive()) {
      log.error(
        'setDryRunMode',
        'Machine is running. Cannot leave dry run mode.',
        {
          discreteInputs: this.state.discreteInputs,
        },
      );

      return Promise.resolve();
    }

    // Set the top-level state.
    this.state.isDryRunMode = isDryRunMode;

    return Promise.resolve();
  }

  /**
   * Disarm live weld. Dry run is our own in-memory flag rather than machine
   * state (see `setDryRunMode`), so nothing on the welder re-arms it and
   * nothing but this clears it once a run is under way.
   */
  public override resetToSafeState(): Promise<void> {
    // Routed through `setDryRunMode` rather than writing the flag directly, so
    // the guard there stays the single choke point between here and the
    // `ContactorOn` write in `startArc`. Anything added to that method — a
    // `stateChange` emit, say — then covers the reset path too.
    return this.setDryRunMode(
      { kind: 'setDryRunMode', isDryRunMode: true },
      'Robot left normal running; returning welder to dry run.',
    );
  }

  /**
   * Check if the weld machine is ready to accept commands.
   *
   * 3.4.2.12. Ready (Discrete Input)
   * Indicates when the Deltaweld is ready to process commands from the robot interface.
   */
  private assertReady() {
    if (!this.state.discreteInputs.ready) {
      throw new Error(
        'Cannot execute command: Weld machine is not ready to accept commands.',
      );
    }
  }

  private machineIsActive() {
    return (
      this.state.discreteInputs.contactorStatus ||
      this.state.discreteInputs.weldStateStatus ||
      this.state.discreteInputs.feedForwardStatus ||
      this.state.discreteInputs.feedRetractStatus ||
      this.state.discreteInputs.gasStatus ||
      this.state.discreteInputs.arcDetected
    );
  }

  public async initializeOnce() {
    log.info(`init.start`, `Initializing ${this.kind} device...`);
    this.modbus.setReadOnly(false);
    this.modbus.clearSubscriptions();
    log.info(`initOnce.subscribe`, 'Subscribing to configuration registers...');

    // Initialize equipment state.
    await this.initializeCoils();
    await this.initializeInputRegisters();
    await this.initializeDiscreteInputs();
    await this.initializeHoldingRegisters();

    // Now we'll reset all subscriptions to items we actually want to subscribe to.
    this.modbus.clearSubscriptions();

    log.info(`initOnce.complete`, 'Complete, Subscribing to state registers');

    await this.subscribeToCoils();
    await this.subscribeToDiscreteInputs();
    await this.subscribeToInputRegisters();
    await this.subscribeToHoldingRegisters();

    log.info(`initOnce.complete`, 'Done with initializeOnce');
  }

  private async initializeCoils() {
    log.info('initializeCoils', 'Initializing coils...');

    await this.subscribeToCoils();

    const addresses = this.modbusSubscriptionAddresses.coils;

    const [
      weldDisable,
      contactorOn,
      gasEnable,
      motorForward,
      motorRetract,
      touchSenseEnable,
    ] = addresses.map((address) => this.modbus.getCoilValue(address));

    this.state.coils = {
      weldDisable,
      contactorOn,
      gasEnable,
      motorForward,
      motorRetract,
      touchSenseEnable,
    };

    this.modbus.clearSubscriptions();

    log.info('initializeCoils', 'Coil initialization complete.');
  }

  private async initializeInputRegisters() {
    log.info('initializeInputRegisters', 'Initializing input registers...');

    await this.subscribeToInputRegisters();

    const addresses = this.modbusSubscriptionAddresses.inputRegisters;

    const [
      actualWFS,
      actualArcVoltage,
      actualArcCurrent,
      actualGasFlow,
      actualRawHeat,
      actualHeatInput,
      presetWFS,
      presetVoltage,
      presetArcLength,
      presetArcControl,
      memorySlot,
      activeProcess,
      activeWireType,
      activeWireSize,
      activeGasType,
      errorCode,
    ] = addresses.map((address) => this.modbus.getInputRegisterValue(address));

    this.state.inputRegisters = {
      actualWFS: convertMetersPerSecond.fromInchesPerMinute(actualWFS),
      actualArcVoltage: convertVoltageScale.fromWeldMachine(actualArcVoltage),
      actualArcCurrent,
      actualGasFlow,
      actualRawHeat: convertRawHeat.fromWeldMachine(actualRawHeat),
      actualHeatInput: convertHeatInput.fromWeldMachine(actualHeatInput),
      presetWFS: convertMetersPerSecond.fromInchesPerMinute(presetWFS),
      presetVoltage: convertVoltageScale.fromWeldMachine(presetVoltage),
      presetArcLength: convertArcLength.fromWeldMachine(presetArcLength),
      presetArcControl,
      memorySlot,
      activeProcess:
        WeldMachineModeFromEnum[
          activeProcess as keyof typeof WeldMachineModeFromEnum
        ],
      activeWireType: WireType.parse(activeWireType),
      activeWireSize: WireSize.parse(activeWireSize),
      activeGasType: GasType.parse(activeGasType),
      errorCode,
    };

    this.modbus.clearSubscriptions();

    log.info(
      'initializeInputRegisters',
      'Input register initialization complete.',
    );
  }

  private async initializeDiscreteInputs() {
    log.info('initializeDiscreteInputs', 'Initializing discrete inputs...');

    await this.subscribeToDiscreteInputs();

    const addresses = this.modbusSubscriptionAddresses.discreteInputs;

    const [
      arcDetected,
      contactorStatus,
      gasStatus,
      feedForwardStatus,
      feedRetractStatus,
      touchSenseEnabled,
      wireTouched,
      weldStateStatus,
      synergicProcess,
      errorActive,
      pSConnected,
      ready,
    ] = addresses.map((address) => this.modbus.getDiscreteInputValue(address));

    this.state.discreteInputs = {
      arcDetected,
      contactorStatus,
      gasStatus,
      feedForwardStatus,
      feedRetractStatus,
      touchSenseEnabled,
      wireTouched,
      weldStateStatus,
      synergicProcess,
      errorActive,
      pSConnected,
      ready,
    };

    this.modbus.clearSubscriptions();

    log.info(
      'initializeDiscreteInputs',
      'Discrete input initialization complete.',
    );
  }

  private async initializeHoldingRegisters() {
    log.info('initializeHoldingRegisters', 'Initializing holding registers...');

    await this.subscribeToHoldingRegisters();

    const addresses = this.modbusSubscriptionAddresses.holdingRegisters;

    const [
      presetWFS,
      presetVoltage,
      presetArcLength,
      presetArcControl,
      selectMemorySlot,
      weldProcess,
      weldWireType,
      weldWireSize,
      weldGasType,
      weldTravelSpeed,
    ] = addresses.map((address) => this.modbus.getRegisterValue(address));

    this.state.holdingRegisters = {
      presetWFS: convertMetersPerSecond.fromInchesPerMinute(presetWFS),
      presetVoltage: convertVoltageScale.fromWeldMachine(presetVoltage),
      presetArcLength: convertArcLength.fromWeldMachine(presetArcLength),
      presetArcControl,
      selectMemorySlot,
      weldProcess:
        WeldMachineModeFromEnum[
          weldProcess as keyof typeof WeldMachineModeFromEnum
        ],
      weldWireType: WireType.parse(weldWireType),
      weldWireSize: WireSize.parse(weldWireSize),
      weldGasType: GasType.parse(weldGasType),
      weldTravelSpeed: convertWeldTravelSpeed.fromWeldMachine(weldTravelSpeed),
    };

    this.modbus.clearSubscriptions();

    log.info(
      'initializeHoldingRegisters',
      'Holding register initialization complete.',
    );
  }

  private async subscribeToCoils() {
    const addresses = this.modbusSubscriptionAddresses.coils;

    // wait until all registers have been populated with initial values
    await timeout(
      INITIALIZATION_TIMEOUT_MS,
      'Failed to fetch initial coils from the WeldMachine',
    ).race(
      Promise.all(
        addresses.map((address) => this.modbus.subscribeToCoil(address)),
      ),
    );
  }

  private async subscribeToInputRegisters() {
    const addresses = this.modbusSubscriptionAddresses.inputRegisters;

    // wait until all registers have been populated with initial values
    await timeout(
      INITIALIZATION_TIMEOUT_MS,
      'Failed to fetch initial input registers from the WeldMachine',
    ).race(
      Promise.all(
        addresses.map((address) =>
          this.modbus.subscribeToInputRegister(address),
        ),
      ),
    );
  }

  private async subscribeToDiscreteInputs() {
    const addresses = this.modbusSubscriptionAddresses.discreteInputs;

    await timeout(
      INITIALIZATION_TIMEOUT_MS,
      'Failed to fetch discrete input registers from the WeldMachine',
    ).race(
      Promise.all(
        addresses.map((address) =>
          this.modbus.subscribeToDiscreteInput(address),
        ),
      ),
    );
  }

  private async subscribeToHoldingRegisters() {
    const addresses = this.modbusSubscriptionAddresses.holdingRegisters;

    await timeout(
      INITIALIZATION_TIMEOUT_MS,
      'Failed to fetch holding registers from the WeldMachine',
    ).race(
      Promise.all(
        addresses.map((address) => this.modbus.subscribeToRegister(address)),
      ),
    );
  }

  public listenForChanges() {
    if (this.stopListeningForChanges) {
      this.stopListeningForChanges();
    }

    this.stopListeningForChanges = this.modbus.on(
      'update',
      (address: number, _, msgKind) => {
        try {
          switch (msgKind) {
            case 'holdingRegister': {
              switch (address) {
                case HoldingRegisters.PresetWFS:
                  this.updateHoldingRegisterState(
                    'presetWFS',
                    HoldingRegisters.PresetWFS,
                    convertMetersPerSecond.fromInchesPerMinute,
                  );

                  break;
                case HoldingRegisters.PresetVoltage:
                  this.updateHoldingRegisterState(
                    'presetVoltage',
                    HoldingRegisters.PresetVoltage,
                    convertVoltageScale.fromWeldMachine,
                  );

                  break;

                case HoldingRegisters.PresetArcLength:
                  this.updateHoldingRegisterState(
                    'presetArcLength',
                    HoldingRegisters.PresetArcLength,
                    convertArcLength.fromWeldMachine,
                  );

                  break;

                case HoldingRegisters.PresetArcControl:
                  this.updateHoldingRegisterState(
                    'presetArcControl',
                    HoldingRegisters.PresetArcControl,
                  );

                  break;

                case HoldingRegisters.SelectMemorySlot:
                  this.updateHoldingRegisterState(
                    'selectMemorySlot',
                    HoldingRegisters.SelectMemorySlot,
                  );

                  break;

                case HoldingRegisters.WeldProcess:
                  this.updateHoldingRegisterState(
                    'weldProcess',
                    HoldingRegisters.WeldProcess,
                    (v) =>
                      WeldMachineModeFromEnum[
                        v as keyof typeof WeldMachineModeFromEnum
                      ],
                  );

                  break;

                case HoldingRegisters.WeldWireType:
                  this.updateHoldingRegisterState(
                    'weldWireType',
                    HoldingRegisters.WeldWireType,
                    (v) => WireType.parse(v),
                  );

                  break;

                case HoldingRegisters.WeldWireSize:
                  this.updateHoldingRegisterState(
                    'weldWireSize',
                    HoldingRegisters.WeldWireSize,
                    (v) => WireSize.parse(v),
                  );

                  break;

                case HoldingRegisters.WeldGasType:
                  this.updateHoldingRegisterState(
                    'weldGasType',
                    HoldingRegisters.WeldGasType,
                    (v) => GasType.parse(v),
                  );

                  break;
                case HoldingRegisters.WeldTravelSpeed:
                  this.updateHoldingRegisterState(
                    'weldTravelSpeed',
                    HoldingRegisters.WeldTravelSpeed,
                    convertWeldTravelSpeed.fromWeldMachine,
                  );

                  break;
                default:
                  break;
              }

              break;
            }
            case 'inputRegister': {
              switch (address) {
                case InputRegisters.ActualWFS:
                  this.updateInputRegisterState(
                    'actualWFS',
                    InputRegisters.ActualWFS,
                    convertMetersPerSecond.fromInchesPerMinute,
                  );

                  break;
                case InputRegisters.ActualArcVoltage:
                  this.updateInputRegisterState(
                    'actualArcVoltage',
                    InputRegisters.ActualArcVoltage,
                    convertVoltageScale.fromWeldMachine,
                  );

                  break;

                case InputRegisters.ActualArcCurrent:
                  this.updateInputRegisterState(
                    'actualArcCurrent',
                    InputRegisters.ActualArcCurrent,
                  );

                  break;
                case InputRegisters.ActualGasFlow:
                  this.updateInputRegisterState(
                    'actualGasFlow',
                    InputRegisters.ActualGasFlow,
                  );

                  break;
                case InputRegisters.RawHeat:
                  this.updateInputRegisterState(
                    'actualRawHeat',
                    InputRegisters.RawHeat,
                    convertRawHeat.fromWeldMachine,
                  );

                  break;
                case InputRegisters.HeatInput:
                  this.updateInputRegisterState(
                    'actualHeatInput',
                    InputRegisters.HeatInput,
                    convertHeatInput.fromWeldMachine,
                  );

                  break;
                case InputRegisters.PresetWFS:
                  this.updateInputRegisterState(
                    'presetWFS',
                    InputRegisters.PresetWFS,
                    convertMetersPerSecond.fromInchesPerMinute,
                  );

                  break;
                case InputRegisters.PresetVoltage:
                  this.updateInputRegisterState(
                    'presetVoltage',
                    InputRegisters.PresetVoltage,
                    convertVoltageScale.fromWeldMachine,
                  );

                  break;
                case InputRegisters.PresetArcLength:
                  this.updateInputRegisterState(
                    'presetArcLength',
                    InputRegisters.PresetArcLength,
                    convertArcLength.fromWeldMachine,
                  );

                  break;
                case InputRegisters.PresetArcControl:
                  this.updateInputRegisterState(
                    'presetArcControl',
                    InputRegisters.PresetArcControl,
                  );

                  break;
                case InputRegisters.MemorySlot:
                  this.updateInputRegisterState(
                    'memorySlot',
                    InputRegisters.MemorySlot,
                  );

                  break;
                case InputRegisters.ActiveProcess:
                  this.updateInputRegisterState(
                    'activeProcess',
                    InputRegisters.ActiveProcess,
                    (v) =>
                      WeldMachineModeFromEnum[
                        v as keyof typeof WeldMachineModeFromEnum
                      ],
                  );

                  break;
                case InputRegisters.ActiveWiretype:
                  this.updateInputRegisterState(
                    'activeWireType',
                    InputRegisters.ActiveWiretype,
                    (v) => WireType.parse(v),
                  );

                  break;
                case InputRegisters.ActiveWireSize:
                  this.updateInputRegisterState(
                    'activeWireSize',
                    InputRegisters.ActiveWireSize,
                    (v) => WireSize.parse(v),
                  );

                  break;
                case InputRegisters.ActiveGasType:
                  this.updateInputRegisterState(
                    'activeGasType',
                    InputRegisters.ActiveGasType,
                    (v) => GasType.parse(v),
                  );

                  break;
                case InputRegisters.ErrorCode:
                  this.updateInputRegisterState(
                    'errorCode',
                    InputRegisters.ErrorCode,
                  );

                  break;

                default:
                  break;
              }

              break;
            }
            case 'discreteInput': {
              switch (address) {
                case DiscreteInputs.ArcDetected:
                  this.updateDiscreteInputsState('arcDetected', address);

                  break;
                case DiscreteInputs.ContactorStatus:
                  this.updateDiscreteInputsState('contactorStatus', address);

                  break;
                case DiscreteInputs.GasStatus:
                  this.updateDiscreteInputsState('gasStatus', address);

                  break;
                case DiscreteInputs.FeedForwardStatus:
                  this.updateDiscreteInputsState('feedForwardStatus', address);
                  break;
                case DiscreteInputs.FeedRetractStatus:
                  this.updateDiscreteInputsState('feedRetractStatus', address);

                  break;
                case DiscreteInputs.TouchSenseEnabled:
                  this.updateDiscreteInputsState('touchSenseEnabled', address);

                  break;
                case DiscreteInputs.WireTouched:
                  this.updateDiscreteInputsState('wireTouched', address);

                  break;
                case DiscreteInputs.WeldStateStatus:
                  this.updateDiscreteInputsState('weldStateStatus', address);

                  break;
                case DiscreteInputs.SynergicProcess:
                  this.updateDiscreteInputsState('synergicProcess', address);

                  break;
                case DiscreteInputs.ErrorActive:
                  this.updateDiscreteInputsState('errorActive', address);

                  break;
                case DiscreteInputs.PSConnected:
                  this.updateDiscreteInputsState('pSConnected', address);

                  break;
                case DiscreteInputs.Ready:
                  this.updateDiscreteInputsState('ready', address);
                  break;

                default:
                  break;
              }

              break;
            }

            case 'coil': {
              switch (address) {
                case Coils.WeldDisable:
                  this.updateCoilsState('weldDisable', address);
                  break;
                case Coils.ContactorOn:
                  this.updateCoilsState('contactorOn', address);
                  break;

                case Coils.GasEnable:
                  this.updateCoilsState('gasEnable', address);
                  break;
                case Coils.MotorForward:
                  this.updateCoilsState('motorForward', address);
                  break;
                case Coils.MotorRetract:
                  this.updateCoilsState('motorRetract', address);
                  break;
                case Coils.TouchSenseEnable:
                  this.updateCoilsState('touchSenseEnable', address);
                  break;
                default:
                  break;
              }

              break;
            }
            default:
              break;
          }
        } catch (err) {
          log.warn(
            `listenForChanges.error`,
            'Caught error in listenForChanges',
            err,
          );
        }
      },
    );
  }

  private updateHoldingRegisterState<
    K extends keyof MillerWeldMachineState['holdingRegisters'],
  >(
    key: K,
    address: HoldingRegisters,
    transform?: (v: number) => MillerWeldMachineState['holdingRegisters'][K],
  ) {
    let value: number | MillerWeldMachineState['holdingRegisters'][K];
    value = this.modbus.getRegisterValue(address);

    if (transform != null) {
      value = transform(value);
    }

    if (this.state.holdingRegisters[key] !== value) {
      this.state.holdingRegisters[key] =
        value as MillerWeldMachineState['holdingRegisters'][K];

      this.emit('stateChange');
    }
  }

  private updateInputRegisterState<
    K extends keyof MillerWeldMachineState['inputRegisters'],
  >(
    key: K,
    address: InputRegisters,
    transform?: (v: number) => MillerWeldMachineState['inputRegisters'][K],
  ) {
    let value: number | MillerWeldMachineState['inputRegisters'][K];
    value = this.modbus.getInputRegisterValue(address);

    if (transform != null) {
      value = transform(value);
    }

    if (this.state.inputRegisters[key] !== value) {
      this.state.inputRegisters[key] =
        value as MillerWeldMachineState['inputRegisters'][K];

      this.emit('stateChange');
    }
  }

  private updateDiscreteInputsState<
    K extends keyof MillerWeldMachineState['discreteInputs'],
  >(
    key: keyof MillerWeldMachineState['discreteInputs'],
    address: DiscreteInputs,
    transform?: (v: boolean) => MillerWeldMachineState['discreteInputs'][K],
  ) {
    let value: boolean | MillerWeldMachineState['discreteInputs'][K];
    value = this.modbus.getDiscreteInputValue(address);

    if (transform != null) {
      value = transform(value);
    }

    if (this.state.discreteInputs[key] !== value) {
      this.state.discreteInputs[key] =
        value as MillerWeldMachineState['discreteInputs'][K];

      this.emit('stateChange');
    }
  }

  private updateCoilsState<K extends keyof MillerWeldMachineState['coils']>(
    key: keyof MillerWeldMachineState['coils'],
    address: Coils,
    transform?: (v: boolean) => MillerWeldMachineState['coils'][K],
  ) {
    let value: boolean | MillerWeldMachineState['coils'][K];
    value = this.modbus.getCoilValue(address);

    if (transform != null) {
      value = transform(value);
    }

    if (this.state.coils[key] !== value) {
      this.state.coils[key] = value as MillerWeldMachineState['coils'][K];

      this.emit('stateChange');
    }
  }
}
